import { FormScaffoldProps } from "./types";
import { View, VStack } from "@gluestack-ui/themed";
import { ScrollView } from "@repo/core/components/layout/ScrollView";
import {
  KeyboardAwareScrollView,
  KeyboardStickyView
} from "react-native-keyboard-controller";
import { ScaffoldHeader } from "@repo/core/components/layout/Scaffolds/components/ScaffoldHeader";
import { StatusBar } from "@repo/core/components/layout/StatusBar";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { ScaffoldLoading } from "@repo/core/components/layout/Scaffolds/components/ScaffoldLoading";
import { Platform } from "react-native";

const offset = {
  closed: 0,
  opened: Platform.select({
    ios: 20,
    android: 10
  })
};

export const PageScaffold = ({
  showHeader = true,
  footerContent,
  centerFormContent = false,
  children,
  bg = "$backgroundLight0",
  showBackButtonOnWeb = false,
  title,
  statusBarVariant,
  showCustomStatusBar,
  onBackPress,
  onPressRightIcon,
  rightWebLabel,
  rightIcon,
  rightIconSize,
  rightIconVariant,
  minHeight = "auto",
  loadingTitle,
  loadingDescription,
  headerMarginBottom,
  headerStyle,
  isLoading = false,
  keyboardAware = true,
  isFullWidthContent = false,
  stickyFooter = true,
  loadingContent,
  keyboardAwareBottomOffset = 20,
  keyboardAwareExtraKeyboardSpace = 0,
  keyboardAwareDisableScrollOnKeyboardHide = false,
  stickyFooterOffset = 80,
  ...rest
}: FormScaffoldProps) => {
  const { isTabletOrWeb } = useDimensions();
  const isWeb = Platform.OS === "web";
  const shouldUseKeyboardAware = keyboardAware && !isWeb;
  const shouldUseKeyboardStickyFooter = stickyFooter && !isWeb;

  const ScrollComponent = shouldUseKeyboardAware
    ? KeyboardAwareScrollView
    : ScrollView;

  const keyboardAwareProps = shouldUseKeyboardAware
    ? {
        bottomOffset:
          footerContent && stickyFooter
            ? keyboardAwareBottomOffset + stickyFooterOffset
            : keyboardAwareBottomOffset,
        extraKeyboardSpace: keyboardAwareExtraKeyboardSpace,
        disableScrollOnKeyboardHide: keyboardAwareDisableScrollOnKeyboardHide,
        enabled: true
      }
    : {};

  return (
    <View flex={1} minHeight={minHeight} w="$full" {...rest} bg={bg}>
      <StatusBar
        statusBarVariant={statusBarVariant}
        showCustomStatusBar={showCustomStatusBar}
      />
      {showHeader && (
        <ScaffoldHeader
          title={title}
          headerStyle={headerStyle}
          onBackPress={onBackPress}
          onPressRightIcon={onPressRightIcon}
          rightWebLabel={rightWebLabel}
          showBackButtonOnWeb={showBackButtonOnWeb}
          rightIcon={rightIcon}
          rightIconSize={rightIconSize}
          rightIconVariant={rightIconVariant}
          mb={headerMarginBottom}
        />
      )}
      <VStack
        flex={1}
        w="$full"
        alignItems={isFullWidthContent ? "stretch" : "center"}
        justifyContent="center"
      >
        <VStack
          justifyContent="flex-start"
          flex={1}
          w="$full"
          alignSelf="stretch"
          $base-w="$full"
        >
          {keyboardAware ? (
            <ScrollComponent
              contentContainerStyle={{
                flexGrow: 1,
                width: "100%"
              }}
              w="$full"
              {...keyboardAwareProps}
            >
              <VStack
                px="$4"
                mt="$6"
                flex={1}
                w={isFullWidthContent ? "$full" : isTabletOrWeb ? 480 : "$full"}
                m={
                  !isFullWidthContent
                    ? isTabletOrWeb
                      ? "auto"
                      : undefined
                    : undefined
                }
                alignItems={isFullWidthContent ? "stretch" : "flex-start"}
              >
                {isLoading
                  ? loadingContent || (
                      <ScaffoldLoading
                        title={loadingTitle}
                        description={loadingDescription}
                      />
                    )
                  : children}
              </VStack>
            </ScrollComponent>
          ) : (
            <ScrollView
              keyboardShouldPersistTaps="handled"
              marginBottom={0}
              w="$full"
              px="$4"
              pt="$6"
              contentContainerStyle={{
                flexGrow: 1,
                width: "100%",
                justifyContent: centerFormContent ? "center" : "flex-start",
                paddingBottom: 16
              }}
            >
              <VStack
                flex={1}
                w={isFullWidthContent ? "$full" : isTabletOrWeb ? 480 : "$full"}
                m={
                  !isFullWidthContent
                    ? isTabletOrWeb
                      ? "auto"
                      : undefined
                    : undefined
                }
                alignItems={isFullWidthContent ? "stretch" : "flex-start"}
              >
                {isLoading
                  ? loadingContent || (
                      <ScaffoldLoading
                        title={loadingTitle}
                        description={loadingDescription}
                      />
                    )
                  : children}
              </VStack>
            </ScrollView>
          )}
        </VStack>
      </VStack>
      {!isLoading && footerContent ? (
        shouldUseKeyboardStickyFooter ? (
          <KeyboardStickyView offset={offset}>
            <VStack
              px="$4"
              gap="$4"
              w={isTabletOrWeb ? 480 : "$full"}
              m={isTabletOrWeb ? "auto" : undefined}
            >
              {footerContent}
            </VStack>
          </KeyboardStickyView>
        ) : (
          <VStack
            px="$4"
            gap="$4"
            w={isTabletOrWeb ? 480 : "$full"}
            m={isTabletOrWeb ? "auto" : undefined}
          >
            {footerContent}
          </VStack>
        )
      ) : null}
    </View>
  );
};
